import React from 'react';
import {Theme} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from 'components/Button';
import Paper from '@mui/material/Paper';
import DraggableModal from 'components/DraggableModal';
import TextField from 'components/inputs/TextField';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import IconButton from '@mui/material/IconButton';
import {swapArrayElements} from 'common/utils';
import {useTranslation} from 'react-i18next';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';

const useStyles = makeStyles((theme: Theme) =>
   createStyles({
      root: {
         margin: 'auto',
         marginBottom: theme.spacing(4),
      },
      paper: {
         width: 200,
         height: 230,
         overflow: 'auto',
         borderColor: theme.palette.primary.main,
      },
      button: {
         margin: theme.spacing(0.5, 0),
      },
      list: {
         textAlign: 'center',
      },
   })
);

function not(a: string[], b: string[]) {
   return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: string[], b: string[]) {
   return a.filter((value) => b.indexOf(value) !== -1);
}

type FormikTransferListProps = {
   name: string;
   label: string;
   leftLabel: string;
   rightLabel: string;
   options: {id: string; name: string}[];
};

const FormikTransferListField = ({
   name,
   label,
   leftLabel,
   rightLabel,
   options,
}: FormikTransferListProps) => {
   const classes = useStyles();
   const {t} = useTranslation();
   // const [field, , helpers] = useField<string[]>({name, multiple: true});
   const {field} = useController({name, defaultValue: []});
   const {setValue} = useFormContext();

   const [edit, setEdit] = React.useState(false);
   const [checked, setChecked] = React.useState<string[]>([]);
   const [left, setLeft] = React.useState<string[]>(() =>
      not(
         options.map((option) => option.id),
         field.value
      )
   );
   const [right, setRight] = React.useState<string[]>(field.value);

   const leftChecked = intersection(checked, left);
   const rightChecked = intersection(checked, right);
   const lastRightCheckedIndex = right.indexOf(rightChecked[rightChecked.length - 1]);

   const textFieldValue = field.value.join(', ');

   const handleToggle = (value: string) => () => {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];

      if (currentIndex === -1) {
         newChecked.push(value);
      } else {
         newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
   };

   const handleCheckedRight = () => {
      setRight(right.concat(leftChecked));
      setLeft(not(left, leftChecked));
      setChecked(not(checked, leftChecked));
   };

   const handleCheckedLeft = () => {
      setLeft(left.concat(rightChecked));
      setRight(not(right, rightChecked));
      setChecked(not(checked, rightChecked));
   };
   const handleMoveUp = () => {
      setRight((right) => {
         return swapArrayElements(right, lastRightCheckedIndex - 1, lastRightCheckedIndex);
      });
   };
   const handleMoveDown = () => {
      setRight((right) => {
         return swapArrayElements(right, lastRightCheckedIndex, lastRightCheckedIndex + 1);
      });
   };
   const handleSetValue = () => {
      setValue(name, right, SetValueOptions);
      setEdit(false);
   };
   const customList = (items: string[], label: string) => (
      <>
         <Typography variant={'body2'} gutterBottom>
            {label}
         </Typography>
         <Paper variant={'outlined'} className={classes.paper}>
            <List dense component="div" role="list">
               {items.map((value: string) => {
                  const labelId = `transfer-list-item-${value}-label`;
                  const option = options.find((option) => option.id === value);
                  return (
                     <ListItem key={value} role="listitem" button onClick={handleToggle(value)}>
                        <ListItemIcon>
                           <Checkbox
                              checked={checked.indexOf(value) !== -1}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{'aria-labelledby': labelId}}
                           />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={option?.name} />
                     </ListItem>
                  );
               })}
               <ListItem />
            </List>
         </Paper>
      </>
   );

   return (
      <>
         <TextField
            label={label}
            disabled
            value={textFieldValue}
            InputProps={{
               endAdornment: (
                  <IconButton size={'small'} onClick={() => setEdit(true)}>
                     <EditRoundedIcon />
                  </IconButton>
               ),
            }}
         />
         {edit && (
            <DraggableModal header={'Transfer List'} width={'sm'} onClose={() => setEdit(false)}>
               <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                  className={classes.root}
               >
                  <Grid item className={classes.list}>
                     {customList(left, leftLabel)}
                  </Grid>
                  <Grid item>
                     <Grid container direction="column" alignItems="center">
                        <Button
                           variant="outlined"
                           size="small"
                           className={classes.button}
                           onClick={handleCheckedLeft}
                           disabled={rightChecked.length === 0}
                           aria-label="move selected left"
                        >
                           &lt;
                        </Button>
                        <Button
                           variant="outlined"
                           size="small"
                           className={classes.button}
                           onClick={handleCheckedRight}
                           disabled={leftChecked.length === 0}
                           aria-label="move selected right"
                        >
                           &gt;
                        </Button>
                        <Button
                           variant="outlined"
                           size="small"
                           className={classes.button}
                           onClick={handleMoveUp}
                           disabled={rightChecked.length !== 1 || lastRightCheckedIndex === 0}
                           aria-label="move selected up"
                        >
                           &#129045;
                        </Button>
                        <Button
                           variant="outlined"
                           size="small"
                           className={classes.button}
                           onClick={handleMoveDown}
                           disabled={
                              rightChecked.length !== 1 ||
                              lastRightCheckedIndex === right.length - 1
                           }
                           aria-label="move selected down"
                        >
                           &#129047;
                        </Button>
                     </Grid>
                  </Grid>
                  <Grid item className={classes.list}>
                     {customList(right, rightLabel)}
                  </Grid>
               </Grid>
               <DialogActions>
                  <Button color="primary" onClick={handleSetValue}>
                     {t('Ok')}
                  </Button>
                  <Button autoFocus color="primary" onClick={() => setEdit(false)}>
                     {t('Cancel')}
                  </Button>
               </DialogActions>
            </DraggableModal>
         )}
      </>
   );
};

export default FormikTransferListField;
